import { type FilterFacet, facetCount } from '@croissants/contracts';
import { type PillFamily, type PillState, pillFilter } from '@croissants/ui';

/**
 * A filter over a pill family, one option per WORD (#2004).
 *
 * The approved wording reads several states as one word - Awaiting bakery is
 * four order phases, Offline is three device states - so a filter that listed
 * the states would offer the same word several times. The ticked values stay
 * the STATES the endpoint filters on; the menu shows one option per word,
 * counted over every state it stands for, and ticking it ticks all of them.
 */
export function pillFilterMenu<F extends PillFamily, S extends PillState<F>>(
  family: F,
  states: readonly S[],
  facets: readonly FilterFacet[] | undefined,
): {
  readonly options: { value: S; label: string; count: number }[];
  /** The ticked states, as the options to show ticked. */
  readonly shown: (values: readonly string[]) => S[];
  /** The ticked options, as the states to send. */
  readonly chosen: (values: readonly string[]) => S[];
} {
  const filter = pillFilter(family, states, 'en');
  return {
    options: filter.options.map((option) => ({
      value: option.value,
      label: option.label,
      count: option.states.reduce((sum, state) => sum + facetCount(facets, state), 0),
    })),
    shown: filter.collapse,
    chosen: filter.expand,
  };
}
